import {
  IonButton,
  IonContent,
  IonFooter,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import {
  GestionAusenciaActiva,
  MOTIVO_AUSENCIA_OTRO,
  MOTIVOS_AUSENCIA_FALLBACK,
  OpcionRespuestaGestion,
  getGestionActiva,
  getMotivosAusencia,
  responderGestion,
} from "src/services/gestionAusencia";
import { GESTION_AUSENCIA_REFETCH_EVENT } from "src/hooks/useGestionAusenciaPendiente";
import { getBestPosition } from "src/utils/geo";

// ------------------------------------------------------------
// Tipos locales
// ------------------------------------------------------------

type VistaEstado = "cargando" | "activa" | "ya_respondida" | "no_encontrada" | "error";

// ------------------------------------------------------------
// Etiquetas de opciones de respuesta
// ------------------------------------------------------------

type OpcionRespuesta = {
  codigo: OpcionRespuestaGestion;
  titulo: string;
  descripcion: string;
};

// Opciones mientras el plazo corre: el turno esta por empezar o recien empezo,
// asi que todas hablan en futuro.
const OPCIONES: OpcionRespuesta[] = [
  {
    codigo: "A",
    titulo: "Estoy en camino",
    descripcion: "Voy a llegar al punto de servicio.",
  },
  {
    codigo: "B",
    titulo: "No voy a asistir",
    descripcion: "No puedo concurrir al turno asignado.",
  },
  {
    codigo: "C",
    titulo: "Estoy en el Contrato",
    descripcion: "Estoy en el punto de servicio pero no puedo marcar mi presencia.",
  },
  {
    codigo: "D",
    titulo: "No corresponde asignación",
    descripcion: "El turno o punto de servicio asignado no me corresponde.",
  },
];

// Opciones cuando el plazo ya vencio: el turno quedo atras. Preguntarle si
// "esta en camino" o pedirle el GPS no tiene sentido, porque a esa hora ya
// esta en su casa. Lo unico que corresponde es que cuente que paso.
const OPCIONES_PLAZO_VENCIDO: OpcionRespuesta[] = [
  {
    codigo: "E",
    titulo: "Sí fui al contrato",
    descripcion: "Estuve en mi punto de servicio en el turno indicado.",
  },
  {
    codigo: "B",
    titulo: "No pude asistir",
    descripcion: "No llegué a concurrir al turno. Indicá el motivo.",
  },
];

const TIEMPOS_ESTIMADOS = [10, 20, 30];

// ------------------------------------------------------------
// Componente
// ------------------------------------------------------------

const GestionAusencia: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const gestionIdUrl = parseInt(id, 10);

  const [present] = useIonToast();
  const history = useHistory();

  const [vista, setVista] = useState<VistaEstado>("cargando");
  const [gestion, setGestion] = useState<GestionAusenciaActiva | null>(null);
  const [opcionSeleccionada, setOpcionSeleccionada] = useState<OpcionRespuestaGestion | null>(null);
  const [tiempoEstimado, setTiempoEstimado] = useState<number>(10);
  const [motivoAusencia, setMotivoAusencia] = useState<string>("");
  const [motivoTexto, setMotivoTexto] = useState<string>("");
  // El catalogo lo define EMSYS; la lista local quedo solo como respaldo.
  const [motivos, setMotivos] = useState<string[]>(MOTIVOS_AUSENCIA_FALLBACK);
  const [motivoOtro, setMotivoOtro] = useState<string>(MOTIVO_AUSENCIA_OTRO);
  const [enviando, setEnviando] = useState(false);
  const [respuestaEnviada, setRespuestaEnviada] = useState(false);

  // Con el plazo vencido el turno ya termino: se le ofrecen las opciones en
  // pasado, no las de "estoy en camino".
  const plazoVencido =
    gestion?.estado === "SIN_RESPUESTA" || gestion?.estado === "NO_MARCO";
  const opciones = plazoVencido ? OPCIONES_PLAZO_VENCIDO : OPCIONES;

  // ----------------------------------------------------------
  // Carga inicial: verificar que la gestion esta activa
  // ----------------------------------------------------------
  const cargarGestion = useCallback(async () => {
    setVista("cargando");
    try {
      const activa = await getGestionActiva();

      if (!activa) {
        // No hay ninguna gestion activa para este operario
        setVista("ya_respondida");
        return;
      }

      if (activa.gestion_id !== gestionIdUrl) {
        // La gestion del link ya fue respondida (hay otra activa o ninguna)
        setVista("ya_respondida");
        return;
      }

      setGestion(activa);
      setVista("activa");
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setVista("ya_respondida");
      } else {
        setVista("error");
      }
    }
  }, [gestionIdUrl]);

  useEffect(() => {
    if (!Number.isFinite(gestionIdUrl)) {
      setVista("no_encontrada");
      return;
    }
    cargarGestion();
  }, [cargarGestion, gestionIdUrl]);

  useEffect(() => {
    let vigente = true;
    getMotivosAusencia().then((catalogo) => {
      if (!vigente) return;
      setMotivos(catalogo.motivos);
      setMotivoOtro(catalogo.motivo_otro);
    });
    return () => {
      vigente = false;
    };
  }, []);

  // ----------------------------------------------------------
  // Enviar respuesta
  // ----------------------------------------------------------
  const enviarRespuesta = async () => {
    if (!opcionSeleccionada || enviando || respuestaEnviada) return;

    if (opcionSeleccionada === "B" && !motivoAusencia) {
      present({ message: "Selecciona el motivo de ausencia.", duration: 3000, color: "warning" });
      return;
    }

    if (opcionSeleccionada === "B" && motivoAusencia === motivoOtro && !motivoTexto.trim()) {
      present({ message: "Describe el motivo de tu ausencia.", duration: 3000, color: "warning" });
      return;
    }

    setEnviando(true);

    // Para opcion C: intentar capturar GPS para validacion automatica de presencia.
    // Si el usuario rechaza el permiso o falla la captura, igual mandamos la opcion
    // sin coordenadas y el motor cae al flujo de validacion manual del CC.
    let latitudGps: number | undefined;
    let longitudGps: number | undefined;
    if (opcionSeleccionada === "C") {
      try {
        const pos = await getBestPosition(30, 7000);
        latitudGps = Number(pos.lat);
        longitudGps = Number(pos.lng);
      } catch {
        present({
          message: "No pudimos verificar tu ubicacion. El supervisor revisara tu caso.",
          duration: 4000,
          color: "warning",
        });
      }
    }

    try {
      await responderGestion(gestionIdUrl, {
        opcion: opcionSeleccionada,
        ...(opcionSeleccionada === "A" ? { tiempo_estimado_min: tiempoEstimado } : {}),
        ...(opcionSeleccionada === "B" ? { motivo_ausencia: motivoAusencia } : {}),
        ...(opcionSeleccionada === "B" && motivoAusencia === motivoOtro ? { motivo_texto: motivoTexto.trim() } : {}),
        ...(opcionSeleccionada === "C" && latitudGps !== undefined && longitudGps !== undefined
          ? { latitud: latitudGps, longitud: longitudGps }
          : {}),
      });

      setRespuestaEnviada(true);
      present({ message: "Respuesta registrada correctamente.", duration: 3000, color: "success" });
      // Avisar al guard global que la gestion ya fue respondida para que libere
      // la navegacion antes de redirigir a /home; sino el guard rebota a esta pantalla.
      window.dispatchEvent(new CustomEvent(GESTION_AUSENCIA_REFETCH_EVENT));
      history.replace("/home");
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.info ||
        "No se pudo registrar la respuesta. Intenta nuevamente.";
      present({ message: msg, duration: 4000, color: "danger" });
    } finally {
      setEnviando(false);
    }
  };

  // ----------------------------------------------------------
  // Render: cargando
  // ----------------------------------------------------------
  if (vista === "cargando") {
    return (
      <IonPage>
        <Header titulo="Gestion de Ausencias" />
        <IonLoading isOpen message="Verificando gestion..." />
      </IonPage>
    );
  }

  // ----------------------------------------------------------
  // Render: ya respondida o no encontrada
  // ----------------------------------------------------------
  if (vista === "ya_respondida" || vista === "no_encontrada") {
    return (
      <IonPage>
        <Header titulo="Gestion de Ausencias" />
        <IonContent className="ion-padding">
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <IonText color="medium">
              <h2 style={{ fontWeight: 600 }}>Caso ya procesado</h2>
              <p>
                Esta notificacion ya fue respondida o el caso fue cerrado por el
                Contact Center.
              </p>
              <p>No se requiere ninguna accion adicional de tu parte.</p>
            </IonText>
          </div>
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <div className="ion-padding">
              <IonButton expand="block" fill="outline" onClick={() => history.replace("/home")}>
                Volver al inicio
              </IonButton>
            </div>
          </IonToolbar>
        </IonFooter>
      </IonPage>
    );
  }

  // ----------------------------------------------------------
  // Render: error de red
  // ----------------------------------------------------------
  if (vista === "error") {
    return (
      <IonPage>
        <Header titulo="Gestion de Ausencias" />
        <IonContent className="ion-padding">
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <IonText color="danger">
              <h2>Error de conexion</h2>
              <p>No se pudo obtener la informacion del caso. Verifica tu conexion.</p>
            </IonText>
          </div>
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <div className="ion-padding">
              <IonButton expand="block" onClick={cargarGestion}>
                Reintentar
              </IonButton>
            </div>
          </IonToolbar>
        </IonFooter>
      </IonPage>
    );
  }

  // ----------------------------------------------------------
  // Render: gestion activa — formulario de respuesta
  // ----------------------------------------------------------
  return (
    <IonPage>
      <Header titulo="Gestion de Ausencias" />
      <IonLoading isOpen={enviando} message="Enviando respuesta..." />

      <IonContent className="ion-padding">
        {/* Informacion del turno */}
        {gestion && (
          <div
            style={{
              background: "#f4f4f4",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
            }}
          >
            <IonText>
              <p style={{ margin: 0, fontWeight: 600 }}>{gestion.punto_servicio}</p>
              {gestion.hora_entrada && gestion.hora_salida && (
                <p style={{ margin: "4px 0 0", color: "#666", fontSize: "0.9rem" }}>
                  Turno: {gestion.hora_entrada} — {gestion.hora_salida} | Fecha:{" "}
                  {gestion.fecha_turno}
                </p>
              )}
            </IonText>
          </div>
        )}

        {/* Aviso de plazo vencido: el operario puede responder tarde si CC no toco el caso */}
        {gestion && (gestion.estado === "SIN_RESPUESTA" || gestion.estado === "NO_MARCO") && (
          <div
            style={{
              background: "#FFF3CD",
              border: "1px solid #FFE69C",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 16,
              color: "#664D03",
            }}
          >
            <IonText>
              <p style={{ margin: 0, fontWeight: 600 }}>Plazo vencido</p>
              <p style={{ margin: "4px 0 0", fontSize: "0.9rem" }}>
                {gestion.estado === "SIN_RESPUESTA"
                  ? "No respondiste a tiempo, pero igual necesitamos saber que paso con este turno."
                  : "No encontramos tu marcacion en el tiempo que declaraste. Contanos que paso con este turno."}
              </p>
            </IonText>
          </div>
        )}

        <IonText>
          <h3 style={{ marginBottom: 8 }}>
            {plazoVencido ? "Que paso con este turno?" : "Como se explica tu ausencia?"}
          </h3>
          <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
            {plazoVencido
              ? "Selecciona lo que realmente ocurrio. Vamos a verificarlo con tus marcaciones."
              : "Selecciona la opcion que mejor describe tu situacion actual."}
          </p>
        </IonText>

        {/* Opciones de respuesta */}
        <IonRadioGroup
          value={opcionSeleccionada}
          onIonChange={(e) => setOpcionSeleccionada(e.detail.value as OpcionRespuestaGestion)}
        >
          <IonList lines="full">
            {opciones.map((op) => (
              <IonItem
                key={op.codigo}
                style={{
                  border:
                    opcionSeleccionada === op.codigo
                      ? "2px solid var(--ion-color-primary)"
                      : "2px solid transparent",
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <IonLabel className="ion-text-wrap">
                  <h2>{op.titulo}</h2>
                  <p>{op.descripcion}</p>
                </IonLabel>
                <IonRadio slot="end" value={op.codigo} />
              </IonItem>
            ))}
          </IonList>
        </IonRadioGroup>

        {/* Campo adicional: tiempo estimado (opcion A) */}
        {opcionSeleccionada === "A" && (
          <div style={{ marginTop: 12 }}>
            <IonText>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>
                Tiempo estimado de llegada
              </p>
            </IonText>
            <IonList>
              <IonItem>
                <IonLabel>Minutos estimados</IonLabel>
                <IonSelect
                  value={tiempoEstimado}
                  onIonChange={(e) => setTiempoEstimado(Number(e.detail.value))}
                  interface="action-sheet"
                >
                  {TIEMPOS_ESTIMADOS.map((t) => (
                    <IonSelectOption key={t} value={t}>
                      {t} minutos
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
          </div>
        )}

        {/* Aviso GPS (opcion C) */}
        {opcionSeleccionada === "C" && (
          <div style={{ marginTop: 12 }}>
            <IonText color="medium">
              <p style={{ fontSize: "0.85rem", margin: 0 }}>
                Al confirmar usaremos tu ubicacion para verificar que estas en
                el punto de servicio. Asegurate de tener el GPS encendido.
              </p>
            </IonText>
          </div>
        )}

        {/* Aviso de verificacion (opcion E) */}
        {opcionSeleccionada === "E" && (
          <div style={{ marginTop: 12 }}>
            <IonText color="medium">
              <p style={{ fontSize: "0.85rem", margin: 0 }}>
                Vamos a buscar tu marcacion de ese dia. Si aparece, el caso se
                cierra solo. Si el biometrico de tu punto estuvo sin senal,
                queda registrado. Si no, tu fiscal lo va a confirmar.
              </p>
            </IonText>
          </div>
        )}

        {/* Campo adicional: motivo ausencia (opcion B) */}
        {opcionSeleccionada === "B" && (
          <div style={{ marginTop: 12 }}>
            <IonText>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>
                Motivo de ausencia <span style={{ color: "red" }}>*</span>
              </p>
            </IonText>
            <IonList>
              <IonItem>
                <IonLabel>Motivo</IonLabel>
                <IonSelect
                  value={motivoAusencia}
                  placeholder="Selecciona..."
                  onIonChange={(e) => setMotivoAusencia(String(e.detail.value))}
                  interface="action-sheet"
                >
                  {motivos.map((m) => (
                    <IonSelectOption key={m} value={m}>
                      {m}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
            {/* Texto libre cuando se elige Otro */}
            {motivoAusencia === motivoOtro && (
              <IonItem style={{ marginTop: 8 }}>
                <IonLabel position="floating">Describe el motivo <span style={{ color: "red" }}>*</span></IonLabel>
                <IonTextarea
                  value={motivoTexto}
                  onIonChange={(e) => setMotivoTexto(e.detail.value || "")}
                  rows={3}
                  maxlength={300}
                  placeholder="Escribe aqui el motivo..."
                />
              </IonItem>
            )}
          </div>
        )}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <div className="ion-padding">
            <IonButton
              expand="block"
              disabled={!opcionSeleccionada || enviando || respuestaEnviada}
              onClick={enviarRespuesta}
            >
              Confirmar respuesta
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default GestionAusencia;
